import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MemberReturn, MemberCreate, BulkMemberMessageRequest } from '../clients/rust-api-client';
import { memberService, notificationService } from '../clients/ApiClients';
import LoadingSpinner from '../LoadingSpinner';
import {
  getCompanyIdFromToken,
  getUserTypeFromToken,
} from '../utils/TokenUtils';
import { useSenderName } from '../hooks/UserContext';
import { Heading } from '../common/Catalyst/heading';
import { hasAdminAccess } from '../constants/UserTypes';
import { EnvelopeIcon, TrashIcon } from '@heroicons/react/16/solid';
import ShowAlert from '../ShowAlert';
import { ListFilterBar } from '../shell/ListFilterBar';
import { CreateButton } from '../shell/CreateButton';
import { SelectionBar } from '../violation/linear/RowCheckbox';
import { GroupedList } from '../shell/GroupedList';
import {
  MEMBER_STATUSES,
  MemberFilters,
  applyMemberFilters,
  groupMembers,
  memberInitials,
  memberListName,
} from './memberMeta';
import AddMemberDialog from './AddMemberDialog';
import { useSyncedMembers } from '../sync/hooks';
import { HomeReturn } from '../clients/rust-api-client';
import AddMemberToHomeDialog from './AddMemberToHomeDialog';
import SendBulkMessageDialog from './SendBulkMessageDialog';
import { apiErrorMessage } from '../utils/apiErrors';

interface MemberListComponentProps {
  hoaId?: string;
  home?: HomeReturn | null;
  fetchAll: boolean;
}

interface AlertState {
  type: 'success' | 'error';
  title: string;
  message: string;
  show: boolean;
}

const MemberListComponent: React.FC<MemberListComponentProps> = ({
  hoaId,
  home,
  fetchAll,
}) => {
  const navigate = useNavigate();
  const [fetchedMembers, setMembers] = useState<MemberReturn[]>([]);
  const [loading, setLoading] = useState(true);
  // Linear-style filters (#603 follow-on 3).
  const [filters, setFilters] = useState<MemberFilters>({ statuses: [], primaryOnly: [], search: '' });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAddToHomeDialogOpen, setIsAddToHomeDialogOpen] = useState(false);
  const [isBulkMessageDialogOpen, setIsBulkMessageDialogOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(
    new Set()
  );
  const [alert, setAlert] = useState<AlertState>({
    type: 'success',
    title: '',
    message: '',
    show: false,
  });

  const companyId = useMemo(() => getCompanyIdFromToken(), []);
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const senderName = useSenderName();

  const hideAlert = () => {
    setAlert((prev) => ({ ...prev, show: false }));
  };

  const showAlert = (
    type: 'success' | 'error',
    title: string,
    message: string
  ) => {
    setAlert({
      type,
      title,
      message,
      show: true,
    });
  };

  // The sync engine's local copy when it has one (#561); fetching below is
  // the fallback. Members reach the copy through their homes, so an
  // association's members are the members of its homes — what the
  // endpoint answers too.
  const synced = useSyncedMembers(
    fetchAll && hoaId ? { hoaId } : { homeId: home?.id }
  );
  const fromStore = synced !== null && !!((fetchAll && hoaId) || home);
  const members = fromStore && synced ? synced : fetchedMembers;

  useEffect(() => {
    const fetchMembers = async () => {
      if (fromStore) {
        setLoading(false);
        return;
      }
      try {
        if (fetchAll && hoaId) {
          setLoading(true);
          const response = await memberService.getMembersByHoa({ hoaId });
          setMembers(response.data);
        } else if (home) {
          setLoading(true);
          const response = await memberService.getMembersByHome({
            homeId: home.id,
          });
          setMembers(response.data);
        }
      } catch (err) {
        console.error('Error fetching members:', err);
        const errorMessage = apiErrorMessage(err, "Couldn't load members.");

        showAlert('error', "Couldn't load members", errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [fetchAll, hoaId, home, fromStore]);

  const visibleMembers = useMemo(() => applyMemberFilters(members, filters), [members, filters]);
  const groups = useMemo(() => groupMembers(visibleMembers), [visibleMembers]);

  const handleRowClick = (member: MemberReturn) => {
    navigate(`/hoa/${hoaId}/member/${member.id}`);
  };

  const handleAddMember = async (newMember: MemberCreate) => {
    try {
      const response = await memberService.createMember({
        memberCreate: newMember,
      });
      setMembers((prevMembers) => [...prevMembers, response.data.member]);

      // **Show what the backend actually did, not a fixed sentence.**
      // Creating a member now sends a WorkOS invitation, and there are
      // four outcomes: invited, an invitation was already outstanding,
      // activated without one (the address already had a sign-in), or
      // the invitation could not be sent at all.
      //
      // That last case is why this matters. The member exists, is
      // inactive, and nothing was emailed — and only the person reading
      // this alert can know. A homeowner who never receives an
      // invitation has nothing to report; they are simply waiting.
      // `setup_email_sent` is false exactly then, so it drives the
      // severity as well as the wording.
      const created = response.data;
      // `error` rather than a softer level, because this one needs
      // acting on — `ShowAlert` has no middle severity, and silence is
      // the failure mode being avoided. The title still says the member
      // exists, so it cannot read as "nothing happened".
      showAlert(
        created.setup_email_sent ? 'success' : 'error',
        created.setup_email_sent
          ? 'Member created'
          : 'Member created — invitation not sent',
        created.message ?? 'Member created successfully!'
      );
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Error creating member:', err);
      const errorMessage = apiErrorMessage(err, "Couldn't create the member.");

      showAlert('error', "Couldn't create member", errorMessage);
    }
  };

  const handleAddMemberToHome = async (data: {
    homeId: string;
    memberId: string;
  }) => {
    try {
      await memberService.addMemberToHome({
        homeId: data.homeId,
        memberId: data.memberId,
      });
      showAlert('success', 'Success', 'Member added to home successfully!');
      // Refresh the members list
      if (home) {
        const response = await memberService.getMembersByHome({
          homeId: home.id,
        });
        setMembers(response.data);
      }
    } catch (err) {
      console.error('Error adding member to home:', err);
      const errorMessage = apiErrorMessage(
        err,
        "Couldn't add the member to this home."
      );
      showAlert('error', "Couldn't add member to home", errorMessage);
    }
  };

  const handleRemoveMembers = async () => {
    if (selectedMembers.size === 0 || !home) return;

    try {
      // Create an array of promises for each member removal
      const removalPromises = Array.from(selectedMembers).map((memberId) =>
        memberService.removeMemberFromHome({
          homeId: home.id,
          memberId: memberId,
        })
      );

      await Promise.all(removalPromises);

      // Refresh the members list
      const response = await memberService.getMembersByHome({
        homeId: home.id,
      });
      setMembers(response.data);
      setSelectedMembers(new Set()); // Clear selection
      showAlert(
        'success',
        'Success',
        'Members removed from home successfully!'
      );
    } catch (err) {
      console.error('Error removing members from home:', err);
      const errorMessage = apiErrorMessage(err, "Couldn't remove the members.");
      showAlert('error', "Couldn't remove members", errorMessage);
    }
  };

  // A failed request is left to propagate: the dialog stays open and says
  // why. A partial send did go out, so it closes and reports here.
  const handleSendBulkMessage = async (messageData: BulkMemberMessageRequest) => {
    const response = await notificationService.sendBulkMemberMessages({
      bulkMemberMessageRequest: messageData,
    });
    const result = response.data;
    if (result.failed_count > 0) {
      showAlert(
        'error',
        'Partial Success',
        `Sent to ${result.successful_count} members, but ${result.failed_count} failed.`
      );
    } else {
      showAlert(
        'success',
        'Messages Sent',
        `Successfully sent message to ${result.successful_count} members.`
      );
    }
    setSelectedMembers(new Set());
  };

  // With a synced copy there is nothing to wait for: render it on the
  // first paint, not after the effect clears `loading` (#635).
  if (loading && !fromStore) {
    return <LoadingSpinner />;
  }

  const selectedMemberIds = Array.from(selectedMembers);
  const canAdmin = hasAdminAccess(userType);
  const selectionAction =
    'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-fg-secondary hover:bg-fg/5 hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent';

  return (
    <>
      <Heading level={4} className="mb-3">
        Members
      </Heading>

      <ShowAlert
        alertType={alert.type}
        alertTitle={alert.title}
        alertMessage={alert.message}
        isVisible={alert.show}
        onClose={hideAlert}
        timeout={5000}
        isInline={true}
      />

      <div className="overflow-hidden rounded-lg border border-line bg-raised">
        <ListFilterBar
          fields={[
            {
              key: 'statuses',
              label: 'Status',
              options: MEMBER_STATUSES.map((st) => ({ value: st.value, label: st.label })),
              values: filters.statuses,
            },
            {
              key: 'primaryOnly',
              label: 'Role',
              options: [{ value: 'primary', label: 'Primary owner' }],
              values: filters.primaryOnly,
            },
          ]}
          onFieldChange={(key, values) => setFilters((f) => ({ ...f, [key]: values }))}
          search={filters.search}
          onSearch={(search) => setFilters((f) => ({ ...f, search }))}
          searchLabel="Search members by name, email or phone"
          shown={visibleMembers.length}
          total={members.length}
          trailing={
            // In the list's own bar, as on violations and work orders.
            canAdmin && (
              <>
                {hoaId && (
                  <CreateButton label="New member" onClick={() => setIsDialogOpen(true)} />
                )}
                {home && (
                  <CreateButton label="Add member" onClick={() => setIsAddToHomeDialogOpen(true)} />
                )}
              </>
            )
          }
        />
        {/* What can be done to the ticked rows, where they are ticked. */}
        {(hoaId || (canAdmin && home)) && (
          <SelectionBar count={selectedMembers.size} onClear={() => setSelectedMembers(new Set())}>
            {hoaId && (
              <button
                type="button"
                onClick={() => setIsBulkMessageDialogOpen(true)}
                className={selectionAction}
              >
                <EnvelopeIcon className="h-3.5 w-3.5" aria-hidden />
                Message
              </button>
            )}
            {canAdmin && home && (
              <button
                type="button"
                onClick={handleRemoveMembers}
                className={`${selectionAction} text-danger hover:text-danger`}
              >
                <TrashIcon className="h-3.5 w-3.5" aria-hidden />
                Remove from home
              </button>
            )}
          </SelectionBar>
        )}
        <GroupedList
          groups={groups}
          getKey={(m) => m.id}
          rowLabel={(m) => `${memberListName(m)}, ${m.email}`}
          onOpen={handleRowClick}
          selection={{ selected: selectedMembers, onChange: setSelectedMembers }}
          emptyMessage={members.length === 0 ? 'No members yet.' : 'No members match these filters.'}
          renderRow={(m) => (
            <>
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-fg/10 text-[11px] font-semibold text-fg-secondary"
                aria-hidden
              >
                {memberInitials(m)}
              </span>
              <span className="min-w-0 flex-1 truncate">
                <span className="font-medium text-fg">{memberListName(m)}</span>
                <span className="ml-2 text-fg-muted">{m.email}</span>
              </span>
              {m.phone && (
                <span className="hidden text-[12px] text-fg-muted md:inline">{m.phone}</span>
              )}
              {m.is_primary && (
                <span className="rounded-full border border-line px-2 py-0.5 text-[12px] text-fg-muted">
                  Primary
                </span>
              )}
            </>
          )}
        />
      </div>

      {hoaId && (
        <AddMemberDialog
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onSave={handleAddMember}
          userType={userType}
          companyId={companyId ?? ''}
        />
      )}

      {home && (
        <AddMemberToHomeDialog
          isOpen={isAddToHomeDialogOpen}
          home={home}
          companyId={companyId ?? ''}
          onSave={handleAddMemberToHome}
          onClose={() => setIsAddToHomeDialogOpen(false)}
        />
      )}

      {hoaId && (
        <SendBulkMessageDialog
          isOpen={isBulkMessageDialogOpen}
          onClose={() => setIsBulkMessageDialogOpen(false)}
          onSend={handleSendBulkMessage}
          memberIds={selectedMemberIds}
          hoaId={hoaId}
          senderName={senderName}
        />
      )}
    </>
  );
};

export default MemberListComponent;
